import { uploadFile } from "../../../utils/UploadFile";
import {
  ExpenseEntry,
  ExpenseFormData,
  mapExpenseEntryToApiRequest,
} from "../../vehicleExpense/components/types";
import { bookingApi } from "../apiService";

export const handleExpenseSubmit = async (
  values: ExpenseFormData,
  initialValues: ExpenseFormData,
  id: string,
  rentalJourneyId: string,
  setIsSubmitting: (isSubmitting: boolean) => void,
  showToast: (message: string, type: "success" | "error") => void,
  onSuccess: () => void,
  vehicle?: any,
  selectedFiles?: any
) => {
  try {
    setIsSubmitting(true);
    let promises: Promise<any>[] = [];

    // Get expenses to add
    const expensesToAdd = values.expenses.filter((expense) => expense?.isNew);
    if (expensesToAdd.length > 0) {
      const expenseRequests = expensesToAdd.map((expense) =>
        mapExpenseEntryToApiRequest(
          expense,
          values.vehicleNumber,
          values.vehicleId
        )
      );
      if (expenseRequests.length === 1) {
        promises.push(
          bookingApi.createExpense(
            expenseRequests[0],
            rentalJourneyId || id,
            vehicle?.vehicleId
          )
        );
      } else if (expenseRequests.length > 1) {
        promises.push(
          bookingApi.createBulkExpense(
            expenseRequests,
            rentalJourneyId || id,
            vehicle?.vehicleId
          )
        );
      }
    }

    // Get expenses to update
    const expensesToUpdate = values.expenses.filter(
      (expense) =>
        expense?.isEdited &&
        expense?.id &&
        !expense?.isNew &&
        !expense?.isDeleted
    );
    if (expensesToUpdate.length > 0) {
      const expenseRequests = expensesToUpdate.map((expense) =>
        mapExpenseEntryToApiRequest(
          expense,
          values.vehicleNumber,
          values.vehicleId
        )
      );
      if (expenseRequests.length > 0) {
        promises.push(
          bookingApi.updateBulkExpense(
            expenseRequests,
            rentalJourneyId || id,
            vehicle.vehicleId
          )
        );
      }
    }

    // Get expenses to delete
    const expensesToChangedDelete = expensesToUpdate
      .map((changed) =>
        initialValues.expenses.find((initial) => initial.id === changed.id)
      )
      .filter(Boolean) // Filters out undefined/null
      .map((expense) => ({ ...expense, isEdited: true })) as ExpenseEntry[];

    const expensesToSoftDelete = values.expenses.filter(
      (expense) => expense?.isDeleted
    );

    const expensesToDelete = [
      ...expensesToChangedDelete,
      ...expensesToSoftDelete,
    ];

    if (expensesToDelete.length > 0) {
      const expenseRequests = expensesToDelete.map((expense) => {
        return mapExpenseEntryToApiRequest(
          expense,
          values.vehicleNumber,
          values.vehicleId
        );
      });
      if (expenseRequests.length > 0) {
        promises.push(
          bookingApi.deleteBulkUpdatedExpense(
            expenseRequests,
            rentalJourneyId || id
          )
        );
      }
    }

    const newPayments = values.expenses.flatMap((expense) =>
      (expense.payments || []).flatMap((payment) =>
        !expense?.isNew &&
        payment?.isNew &&
        payment?.rentalJourneyExpenseId !== null
          ? [payment]
          : []
      )
    );

    if (newPayments.length > 0) {
      promises.push(bookingApi.createBulkExpensePayment(newPayments));
    }

    // Get payments to update
    const paymentToUpdate = values.expenses.flatMap((expense) => {
      return expense.payments?.flatMap((payment) => {
        if (payment?.id && payment?.isEdited) {
          return payment;
        }
        return [];
      });
    });
    if (paymentToUpdate.length > 0) {
      promises.push(bookingApi.updatedExpensePayment(paymentToUpdate));
    }

    // Get payments to create
    const initialPayment = initialValues.expenses.flatMap((expense) =>
      expense.payments?.flatMap((payment) => {
        if (payment?.id) {
          return payment;
        }
        return null;
      })
    );

    const paymentToCreatedDelete = paymentToUpdate.map((changed) =>
      initialPayment?.find((initial) => initial?.id === changed?.id)
    );
    if (paymentToCreatedDelete.length > 0) {
      promises.push(
        bookingApi.deleteBulkUpdatedExpensePayment(paymentToCreatedDelete)
      );
    }

    const paymentToSoftDelete = values.expenses.flatMap((expense) =>
      expense.payments?.flatMap((payment) => {
        if (payment?.isDeleted) {
          return payment;
        }
        return [];
      })
    );
    if (paymentToSoftDelete.length > 0) {
      promises.push(
        bookingApi.deleteBulkUpdatedExpensePayment(paymentToSoftDelete)
      );
    }

    const uploadPromises = values.expenses.flatMap(
      (expense) =>
        expense.selectedFiles?.map((file: File) =>
          uploadFile({
            file: file,
            documentType: "rental-expense",
            documentTypeId: expense.id || "",
            documentContext: "rental-journey",
            description: "Expense proof for rental journey",
          })
        ) || []
    );
    if (uploadPromises.length > 0) {
      await Promise.all(uploadPromises);
    }

    if (promises.length > 0) {
      const results = await Promise.all(promises);
      showToast("Expenses saved successfully", "success");
    }

    setIsSubmitting(false);
    onSuccess();
  } catch (error) {
    console.error("Error saving expenses:", error);
    showToast("Error saving expenses", "error");
    setIsSubmitting(false);
  }
};
